import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { calculateNutritionPlan } from '../../services/nutritionService';
import { event as gaEvent } from '../../utils/analytics';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { bodyWeightInputBounds, displayWeightToLbs, lbsToDisplayWeight } from '../../utils/weightUnits';
import './Step1Goals.css';

const MACRO_STEPS = {
  dailyCalories: 25,
  dailyProtein: 5,
  dailyCarbs: 5,
  dailyFat: 5
};

const Step1Goals = ({ goals, results, onGoalsChange, onResultsChange, macroOverrides, onMacroOverridesChange }) => {
  const weightUnit = useWeightUnit();
  const weightBounds = bodyWeightInputBounds(weightUnit);
  const [loading, setLoading] = useState(false);
  const [showFormula, setShowFormula] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    onGoalsChange({
      ...goals,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    });
  };

  const handleCalculate = async () => {
    if (!goals.currentWeight || !goals.goalWeight) {
      toast.error('Please enter your current and goal weight');
      return;
    }

    setLoading(true);
    try {
      const result = await calculateNutritionPlan(goals);
      onResultsChange(result);
      // Clear any existing overrides when recalculating
      if (onMacroOverridesChange) {
        onMacroOverridesChange(null);
      }
      toast.success('Nutrition targets calculated!');
      gaEvent({ action: 'calculate_nutrition', category: 'Nutrition', label: goals.goalType });
    } catch (error) {
      toast.error(error.message || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  // Get effective macro value (override or calculated)
  const getEffectiveValue = (macro) => {
    if (macroOverrides && macroOverrides[macro] !== null && macroOverrides[macro] !== undefined) {
      return macroOverrides[macro];
    }
    if (!results?.dailyTargets) return 0;
    return results.dailyTargets[macro];
  };

  const isOverridden = (macro) => {
    return macroOverrides && macroOverrides[macro] !== null && macroOverrides[macro] !== undefined;
  };

  const handleMacroAdjust = (macro, direction) => {
    const step = MACRO_STEPS[macro];
    const currentValue = getEffectiveValue(macro);
    const newValue = direction === 'up' ? currentValue + step : Math.max(step, currentValue - step);
    
    const newOverrides = {
      ...(macroOverrides || {}),
      [macro]: Math.round(newValue)
    };

    // When a gram-based macro changes, recalculate calories to stay in sync
    // Protein & Carbs = 4 cal/g, Fat = 9 cal/g
    if (macro !== 'dailyCalories') {
      const protein = macro === 'dailyProtein' ? Math.round(newValue) : getEffectiveValue('dailyProtein');
      const carbs   = macro === 'dailyCarbs'   ? Math.round(newValue) : getEffectiveValue('dailyCarbs');
      const fat     = macro === 'dailyFat'     ? Math.round(newValue) : getEffectiveValue('dailyFat');
      newOverrides.dailyCalories = Math.round((protein * 4) + (carbs * 4) + (fat * 9));
    }
    
    if (onMacroOverridesChange) {
      onMacroOverridesChange(newOverrides);
      gaEvent({ action: 'adjust_macro', category: 'Nutrition', label: macro, value: Math.round(newValue) });
    }
  };

  const handleResetMacro = (macro) => {
    if (!onMacroOverridesChange) return;
    const newOverrides = { ...(macroOverrides || {}) };
    newOverrides[macro] = null;

    // When resetting a gram-based macro, recalculate calorie override to stay in sync
    if (macro !== 'dailyCalories' && results?.dailyTargets) {
      const protein = macro === 'dailyProtein' ? results.dailyTargets.dailyProtein : getEffectiveValue('dailyProtein');
      const carbs   = macro === 'dailyCarbs'   ? results.dailyTargets.dailyCarbs   : getEffectiveValue('dailyCarbs');
      const fat     = macro === 'dailyFat'     ? results.dailyTargets.dailyFat     : getEffectiveValue('dailyFat');
      const syncedCalories = Math.round((protein * 4) + (carbs * 4) + (fat * 9));
      // Only keep the calorie override if it differs from the original
      if (syncedCalories !== results.dailyTargets.dailyCalories) {
        newOverrides.dailyCalories = syncedCalories;
      } else {
        newOverrides.dailyCalories = null;
      }
    }
    
    const allNull = Object.values(newOverrides).every(v => v === null || v === undefined);
    onMacroOverridesChange(allNull ? null : newOverrides);
  };

  // Get effective per-meal values
  const getEffectivePerMeal = () => {
    const mealsPerDay = goals.mealsPerDay || 4;
    return {
      perMealCalories: Math.round(getEffectiveValue('dailyCalories') / mealsPerDay),
      perMealProtein: Math.round(getEffectiveValue('dailyProtein') / mealsPerDay),
      perMealCarbs: Math.round(getEffectiveValue('dailyCarbs') / mealsPerDay),
      perMealFat: Math.round(getEffectiveValue('dailyFat') / mealsPerDay)
    };
  };

  const goalTypeDescriptions = {
    bulk: 'Build muscle with a calorie surplus and high protein',
    maintain: 'Maintain current weight with balanced macros',
    cut: 'Lose fat while preserving muscle with high protein'
  };

  const activityDescriptions = {
    sedentary: 'Little or no exercise',
    light: 'Light exercise 1-3 days/week',
    moderate: 'Moderate exercise 3-5 days/week',
    active: 'Heavy exercise 6-7 days/week',
    very_active: 'Very heavy exercise, physical job'
  };

  const activityMultipliers = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9
  };

  const calorieAdjustments = {
    bulk: '+400',
    maintain: '0',
    cut: '-400'
  };

  const proteinFactors = {
    bulk: '1.0',
    maintain: '0.85',
    cut: '1.1'
  };

  const macroRatios = {
    bulk: { fat: '25%', carbs: '75%' },
    maintain: { fat: '35%', carbs: '65%' },
    cut: { fat: '40%', carbs: '60%' }
  };

  const effectivePerMeal = results ? getEffectivePerMeal() : null;
  const hasOverrides = macroOverrides && Object.values(macroOverrides).some(v => v !== null && v !== undefined);

  return (
    <div className="step1-goals">
      <div className="step-intro">
        <h2><i className="fas fa-bullseye"></i> Set Your Nutrition Goals</h2>
        <p>Tell us about yourself and your fitness goals to calculate personalized nutrition targets.</p>
      </div>

      <div className="goals-form">
        {/* Goal Type */}
        <div className="form-section">
          <h3><i className="fas fa-flag"></i> Your Goal</h3>
          <div className="goal-type-selector">
            {['bulk', 'maintain', 'cut'].map(type => (
              <button
                key={type}
                className={`goal-type-btn ${goals.goalType === type ? 'active' : ''}`}
                onClick={() => onGoalsChange({ ...goals, goalType: type })}
                type="button"
              >
                <span className="goal-icon">
                  {type === 'bulk' && <i className="fas fa-dumbbell"></i>}
                  {type === 'maintain' && <i className="fas fa-balance-scale"></i>}
                  {type === 'cut' && <i className="fas fa-fire"></i>}
                </span>
                <span className="goal-label">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
              </button>
            ))}
          </div>
          <p className="goal-description">{goalTypeDescriptions[goals.goalType]}</p>
        </div>

        {/* Weight */}
        <div className="nutrition-form-section">
          <h3><i className="fas fa-weight"></i> Weight Information</h3>
          <div className="nutrition-form-row">
            <div className="nutrition-form-group">
              <label>Current Weight ({weightUnit})</label>
              <input
                type="number"
                step="0.1"
                name="currentWeight"
                value={lbsToDisplayWeight(goals.currentWeight, weightUnit).value}
                onChange={(e) => onGoalsChange({
                  ...goals,
                  currentWeight: e.target.value === '' ? '' : displayWeightToLbs(e.target.value, weightUnit)
                })}
                placeholder={weightBounds.placeholder}
                min={weightBounds.min}
                max={weightBounds.max}
              />
            </div>
            <div className="nutrition-form-group">
              <label>Goal Weight ({weightUnit})</label>
              <input
                type="number"
                step="0.1"
                name="goalWeight"
                value={lbsToDisplayWeight(goals.goalWeight, weightUnit).value}
                onChange={(e) => onGoalsChange({
                  ...goals,
                  goalWeight: e.target.value === '' ? '' : displayWeightToLbs(e.target.value, weightUnit)
                })}
                placeholder={weightUnit === 'kg' ? 'e.g. 79' : 'e.g., 175'}
                min={weightBounds.min}
                max={weightBounds.max}
              />
            </div>
          </div>
        </div>

        {/* Target Date - Timeline (only for cut/bulk) */}
        {(goals.goalType === 'cut' || goals.goalType === 'bulk') && (
          <div className="nutrition-form-section">
            <h3><i className="fas fa-calendar-alt"></i> Target Timeline <span className="optional-badge">Optional</span></h3>
            <p className="timeline-description">
              Set a target date to calculate a personalized daily calorie adjustment based on your timeline.
            </p>
            <div className="nutrition-form-row">
              <div className="nutrition-form-group">
                <label>Target Date</label>
                <input
                  type="date"
                  name="targetDate"
                  value={goals.targetDate || ''}
                  onChange={handleInputChange}
                  min={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                />
              </div>
              {goals.targetDate && goals.currentWeight && goals.goalWeight && (
                <div className="nutrition-form-group timeline-preview">
                  <label>Timeline Preview</label>
                  <div className="timeline-info">
                    {(() => {
                      const now = new Date();
                      const target = new Date(goals.targetDate);
                      const weeks = Math.max(1, Math.round((target - now) / (7 * 24 * 60 * 60 * 1000)));
                      const weightDiff = Math.abs(goals.goalWeight - goals.currentWeight);
                      const weeklyRate = weightDiff / weeks;
                      const isCut = goals.goalType === 'cut';
                      const maxSafeRate = isCut ? 2 : 1;
                      const isAggressive = weeklyRate > maxSafeRate;
                      return (
                        <>
                          <span className="timeline-weeks">{weeks} weeks</span>
                          <span className={`timeline-rate ${isAggressive ? 'aggressive' : 'safe'}`}>
                            ~{weeklyRate.toFixed(1)} lb/week {isCut ? 'loss' : 'gain'}
                          </span>
                          {isAggressive && (
                            <span className="timeline-warning">
                              <i className="fas fa-exclamation-triangle"></i> Rate will be capped at {maxSafeRate} lb/week for safety
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            <p className="timeline-examples">
              <i className="fas fa-lightbulb"></i> Examples: Wedding in 3 months, bodybuilding competition in 6 weeks, marathon next spring
            </p>
            {goals.targetDate && (
              <button
                type="button"
                className="clear-timeline-btn"
                onClick={() => onGoalsChange({ ...goals, targetDate: '' })}
              >
                <i className="fas fa-times"></i> Clear target date (use default ±400 cal adjustment)
              </button>
            )}
          </div>
        )}

        {/* Personal Details */}
        <div className="nutrition-form-section">
          <h3><i className="fas fa-user"></i> Personal Details</h3>
          <div className="nutrition-form-row nutrition-form-row-3">
            <div className="nutrition-form-group">
              <label>Gender</label>
              <select name="gender" value={goals.gender} onChange={handleInputChange}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="nutrition-form-group">
              <label>Age</label>
              <input
                type="number"
                name="age"
                value={goals.age}
                onChange={handleInputChange}
                min="13"
                max="120"
              />
            </div>
            <div className="nutrition-form-group">
              <label>Height (inches)</label>
              <input
                type="number"
                name="height"
                value={goals.height}
                onChange={handleInputChange}
                min="36"
                max="96"
                placeholder="e.g., 70"
              />
            </div>
          </div>
        </div>

        {/* Activity Level */}
        <div className="nutrition-form-section">
          <h3><i className="fas fa-running"></i> Activity Level</h3>
          <select
            name="activityLevel"
            value={goals.activityLevel}
            onChange={handleInputChange}
            className="activity-select"
          >
            {Object.entries(activityDescriptions).map(([value, label]) => (
              <option key={value} value={value}>
                {value.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} - {label}
              </option>
            ))}
          </select>
        </div>

        {/* Meals Per Day */}
        <div className="nutrition-form-section">
          <h3><i className="fas fa-clock"></i> Meals Per Day</h3>
          <div className="meals-selector">
            {[3, 4, 5, 6].map(num => (
              <button
                key={num}
                type="button"
                className={`meals-btn ${goals.mealsPerDay === num ? 'active' : ''}`}
                onClick={() => onGoalsChange({ ...goals, mealsPerDay: num })}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* Calculate Button */}
        <div className="nutrition-calculate-section">
          <button
            className="nutrition-calculate-btn"
            onClick={handleCalculate}
            disabled={loading}
            type="button"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Calculating...
              </>
            ) : (
              <>
                <i className="fas fa-calculator"></i> Calculate My Targets
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results Display */}
      {results && (
        <div className="step1-results">
          <h3><i className="fas fa-chart-pie"></i> Your Daily Targets</h3>
          
          {/* Timeline Summary Banner */}
          {results.calculations?.timeline && (
            <div className="timeline-summary-banner">
              <i className="fas fa-calendar-check"></i>
              <div className="timeline-summary-content">
                <strong>{results.calculations.timeline.weeks} weeks</strong> to reach your goal weight
                <span className="timeline-summary-detail">
                  ~{results.calculations.timeline.weeklyRate} lb/week | {results.calculations.timeline.dailyAdjustment > 0 ? '+' : ''}{results.calculations.timeline.dailyAdjustment} cal/day adjustment
                </span>
              </div>
              {results.calculations.timeline.warning && (
                <div className="timeline-summary-warning">
                  <i className="fas fa-exclamation-triangle"></i> {results.calculations.timeline.warning}
                </div>
              )}
            </div>
          )}
          
          {/* Formula Breakdown Toggle */}
          <div className="formula-toggle-section">
            <button 
              className="formula-toggle-btn"
              onClick={() => setShowFormula(!showFormula)}
              type="button"
            >
              <i className={`fas fa-${showFormula ? 'chevron-up' : 'chevron-down'}`}></i>
              <i className="fas fa-flask"></i>
              {showFormula ? 'Hide' : 'Show'} How Targets Are Calculated
            </button>
          </div>

          {/* Formula Breakdown */}
          {showFormula && results.calculations && (
            <div className="formula-breakdown">
              <div className="formula-step">
                <div className="formula-step-header">
                  <span className="formula-step-num">1</span>
                  <span className="formula-step-title">Basal Metabolic Rate (BMR)</span>
                </div>
                <div className="formula-step-body">
                  <div className="formula-equation">
                    <span className="formula-label">Mifflin-St Jeor Equation:</span>
                    <code>
                      {goals.gender === 'female' 
                        ? `(10 x ${Math.round(goals.currentWeight * 0.453592)}kg) + (6.25 x ${Math.round(goals.height * 2.54)}cm) - (5 x ${goals.age}) - 161`
                        : `(10 x ${Math.round(goals.currentWeight * 0.453592)}kg) + (6.25 x ${Math.round(goals.height * 2.54)}cm) - (5 x ${goals.age}) + 5`
                      }
                    </code>
                  </div>
                  <div className="formula-result">
                    <strong>BMR = {results.calculations.bmr} cal/day</strong>
                  </div>
                </div>
              </div>

              <div className="formula-step">
                <div className="formula-step-header">
                  <span className="formula-step-num">2</span>
                  <span className="formula-step-title">Total Daily Energy Expenditure (TDEE)</span>
                </div>
                <div className="formula-step-body">
                  <div className="formula-equation">
                    <code>BMR ({results.calculations.bmr}) x Activity Multiplier ({activityMultipliers[goals.activityLevel]})</code>
                    <span className="formula-note">Activity: {activityDescriptions[goals.activityLevel]}</span>
                  </div>
                  <div className="formula-result">
                    <strong>TDEE = {results.calculations.tdee} cal/day</strong>
                  </div>
                </div>
              </div>

              <div className="formula-step">
                <div className="formula-step-header">
                  <span className="formula-step-num">3</span>
                  <span className="formula-step-title">Daily Calorie Target</span>
                </div>
                <div className="formula-step-body">
                  <div className="formula-equation">
                    {results.calculations.timeline ? (
                      <>
                        <code>TDEE ({results.calculations.tdee}) + Timeline Adjustment ({results.calculations.timeline.dailyAdjustment > 0 ? '+' : ''}{results.calculations.timeline.dailyAdjustment})</code>
                        <span className="formula-note">
                          {results.calculations.timeline.weeks} weeks to goal at ~{results.calculations.timeline.weeklyRate} lb/week
                        </span>
                        {results.calculations.timeline.warning && (
                          <span className="formula-warning">
                            <i className="fas fa-exclamation-triangle"></i> {results.calculations.timeline.warning}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <code>TDEE ({results.calculations.tdee}) + Goal Adjustment ({calorieAdjustments[goals.goalType]})</code>
                        <span className="formula-note">
                          {goals.goalType === 'bulk' && 'Surplus for muscle building'}
                          {goals.goalType === 'maintain' && 'No adjustment for maintenance'}
                          {goals.goalType === 'cut' && 'Deficit for fat loss'}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="formula-result">
                    <strong>Daily Calories = {results.dailyTargets.dailyCalories} cal</strong>
                  </div>
                </div>
              </div>

              <div className="formula-step">
                <div className="formula-step-header">
                  <span className="formula-step-num">4</span>
                  <span className="formula-step-title">Daily Protein Target</span>
                </div>
                <div className="formula-step-body">
                  <div className="formula-equation">
                    <code>Goal Weight ({lbsToDisplayWeight(goals.goalWeight, weightUnit).value} {weightUnit}) x Protein Factor ({proteinFactors[goals.goalType]} g/lb)</code>
                    <span className="formula-note">Range: {results.dailyTargets.dailyProteinMin}g - {results.dailyTargets.dailyProteinMax}g</span>
                  </div>
                  <div className="formula-result">
                    <strong>Daily Protein = {results.dailyTargets.dailyProtein}g</strong>
                  </div>
                </div>
              </div>

              <div className="formula-step">
                <div className="formula-step-header">
                  <span className="formula-step-num">5</span>
                  <span className="formula-step-title">Carbs & Fat Split</span>
                </div>
                <div className="formula-step-body">
                  <div className="formula-equation">
                    <span className="formula-label">Remaining calories after protein:</span>
                    <code>{results.dailyTargets.dailyCalories} - ({results.dailyTargets.dailyProtein}g x 4 cal/g) = {results.dailyTargets.dailyCalories - results.dailyTargets.dailyProtein * 4} cal</code>
                    <span className="formula-note">
                      Split ratio for {goals.goalType}: Fat {macroRatios[goals.goalType]?.fat || '35%'} / Carbs {macroRatios[goals.goalType]?.carbs || '65%'}
                    </span>
                  </div>
                  <div className="formula-result">
                    <strong>Fat = {results.dailyTargets.dailyFat}g (at 9 cal/g) | Carbs = {results.dailyTargets.dailyCarbs}g (at 4 cal/g)</strong>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Adjustable Macro Cards */}
          <div className="step1-targets-grid">
            {[
              { key: 'dailyCalories', label: 'Calories/Day', icon: 'fa-fire-alt', className: 'calories', unit: '' },
              { key: 'dailyProtein', label: 'Protein/Day', icon: 'fa-drumstick-bite', className: 'protein', unit: 'g' },
              { key: 'dailyCarbs', label: 'Carbs/Day', icon: 'fa-bread-slice', className: 'carbs', unit: 'g' },
              { key: 'dailyFat', label: 'Fat/Day', icon: 'fa-cheese', className: 'fat', unit: 'g' }
            ].map(macro => (
              <div key={macro.key} className={`step1-target-card ${macro.className} ${isOverridden(macro.key) ? 'overridden' : ''}`}>
                <div className="step1-target-icon"><i className={`fas ${macro.icon}`}></i></div>
                <div className="step1-target-value-row">
                  <button 
                    className="macro-adjust-btn minus"
                    onClick={() => handleMacroAdjust(macro.key, 'down')}
                    type="button"
                    title={`Decrease by ${MACRO_STEPS[macro.key]}${macro.unit}`}
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <div className="step1-target-value">
                    {getEffectiveValue(macro.key)}{macro.unit}
                  </div>
                  <button 
                    className="macro-adjust-btn plus"
                    onClick={() => handleMacroAdjust(macro.key, 'up')}
                    type="button"
                    title={`Increase by ${MACRO_STEPS[macro.key]}${macro.unit}`}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
                <div className="step1-target-label">{macro.label}</div>
                {isOverridden(macro.key) && (
                  <div className="override-indicator">
                    <span className="override-badge">
                      adjusted from {results.dailyTargets[macro.key]}{macro.unit}
                    </span>
                    <button 
                      className="reset-macro-btn"
                      onClick={() => handleResetMacro(macro.key)}
                      type="button"
                      title="Reset to calculated value"
                    >
                      <i className="fas fa-undo"></i>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {hasOverrides && (
            <div className="overrides-notice">
              <i className="fas fa-info-circle"></i>
              <span>You've manually adjusted some targets. Calories auto-sync when you adjust protein, carbs, or fat (Protein &amp; Carbs = 4 cal/g, Fat = 9 cal/g). Overrides are saved for this plan.</span>
            </div>
          )}

          <div className="step1-per-meal">
            <h4>Per-Meal Targets ({goals.mealsPerDay} meals/day)</h4>
            <div className="step1-per-meal-grid">
              <span><strong>{effectivePerMeal.perMealCalories}</strong> cal</span>
              <span><strong>{effectivePerMeal.perMealProtein}g</strong> protein</span>
              <span><strong>{effectivePerMeal.perMealCarbs}g</strong> carbs</span>
              <span><strong>{effectivePerMeal.perMealFat}g</strong> fat</span>
            </div>
          </div>

          <div className="step1-success">
            <i className="fas fa-check-circle"></i>
            <span>Targets calculated! Click "Next" to start building your meal plan.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step1Goals;
